const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/components/ProgressiveImage/ProgressiveImage.tsx';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { css, cx } from '@emotion/css';
import _ from 'lodash';
import LoadingSpinner from '../LoadingSpinner';

// if true adds 2.5 sec delay to loading large image and 1.5 sec delay to loading small image
const DEBUGDELAY = false;

const imageContainer = css`
    flex: 1;
    min-width: 0px;
    min-height: 0px;
    position: relative;
`;

const imageStyle = css`
    position: absolute;
    height: 100%;
    width: 100%;
    transition: opacity 0.25s linear;
    object-fit: contain;
`;

const imageSmall = css`
    transform: scale(1);
`;

export interface ProgressiveImageProps {
    smallUrl: string /* url to load the thumbnail version */;
    largeUrl: string /* url of the hi-resolution image version */;
    /**
     * This function (normally a useState function) is used in order to check if the image is available
     * @param hasImage
     */
    onImageLoaded?: (hasImage: boolean) => void;
    replacement?: React.ReactNode;
}

const loadURLasBase64 = (url: string, delay: number) =>
    fetch(url)
        .then(response => response.blob())
        .then(
            blob =>
                new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onloadend = () => resolve(reader.result);
                    reader.onerror = reject;
                    if (DEBUGDELAY) {
                        setTimeout(() => reader.readAsDataURL(blob), delay);
                    } else {
                        reader.readAsDataURL(blob);
                    }
                })
        );

interface CachedImage {
    loadingSmall: boolean;
    loadingLarge: boolean;
    imgBlobSmall: string | null;
    imgBlobLarge: string | null;
}

type ImageCache = { [largeUrl: string]: CachedImage };

/**
 * Show an Image by progressively fading from low res thumbnail to hi-res version
 *
 * @param props
 */
const ProgressiveImage = (props: ProgressiveImageProps) => {
    const { smallUrl, largeUrl } = props;
    const [imageCache, setImageCache] = useState<ImageCache>({});

    useEffect(() => {
        const imageData = imageCache[largeUrl];
        if (imageData && (imageData.loadingLarge || imageData.loadingSmall || imageData.imgBlobLarge)) {
            return;
        }

        setImageCache((imageCache: ImageCache) => {
            const newImageCache = _.cloneDeep(imageCache);
            newImageCache[largeUrl] = {
                imgBlobLarge: null,
                imgBlobSmall: null,
                loadingLarge: true,
                loadingSmall: true,
            };
            return newImageCache;
        });

        loadURLasBase64(smallUrl, 1500)
            .then((data: string) => {
                setImageCache((imageCache: ImageCache) => {
                    const newImageCache = _.cloneDeep(imageCache);
                    const imgOK = data.length > 500;
                    if (imgOK) {
                        newImageCache[largeUrl].imgBlobSmall = data;
                    }
                    newImageCache[largeUrl].loadingSmall = false;
                    return newImageCache;
                });
            })
            .catch((_error: any) => {
                setImageCache((imageCache: ImageCache) => {
                    const newImageCache = _.cloneDeep(imageCache);
                    newImageCache[largeUrl].loadingSmall = false;
                    return newImageCache;
                });
            });

        loadURLasBase64(largeUrl, 2500)
            .then((data: string) => {
                setImageCache((imageCache: ImageCache) => {
                    const newImageCache = _.cloneDeep(imageCache);
                    const imgOK = data.length > 500;
                    if (imgOK) {
                        newImageCache[largeUrl].imgBlobLarge = data;
                    }
                    newImageCache[largeUrl].loadingLarge = false;
                    props.onImageLoaded?.(imgOK);
                    return newImageCache;
                });
            })
            .catch((_error: any) => {
                setImageCache((imageCache: ImageCache) => {
                    const newImageCache = _.cloneDeep(imageCache);
                    newImageCache[largeUrl].loadingSmall = false;
                    props.onImageLoaded?.(false);
                    return newImageCache;
                });
            });
    }, [smallUrl, largeUrl]);

    if (!largeUrl) {
        return props.replacement || null;
    }

    const imageData = imageCache[largeUrl];

    if (!imageData) {
        return props.replacement || null;
    }
    if (imageData.loadingSmall && imageData.loadingLarge) {
        return (
            <div className={imageContainer}>
                <div className={imageStyle}>
                    <LoadingSpinner center="absolute" />
                </div>
            </div>
        );
    }

    const imageBlob = imageData.imgBlobLarge || imageData.imgBlobSmall;

    if (!imageBlob) {
        // Here is room for improvement - maybe show a broken image icon or some alert...?
        // Note: this implementation is status quo.
        return props.replacement || null;
    }

    return (
        <div className={imageContainer}>
            <img className={cx(imageStyle, imageData.imgBlobLarge ? null : imageSmall)} src={imageBlob} />
        </div>
    );
};

export default ProgressiveImage;
